import React from 'react';
import { body, caption, fontWeight, Body } from '../../../Constants/Fonts';
import NoDataPage from '../../../Components/NoDataPage';

// RiskCategoryDonut — SVG donut + legend for a categorical breakdown (Risks by
// Category). Ported from the legacy RiskPortal renderDonutList, restyled onto
// the global tokens. Legend text uses the shared Fonts.js presets.
//
// Props: data — [{ label, value, color? }]

// Categorical palette (chart tokens + a few professional extras) — used when a
// slice doesn't carry its own colour.
const PALETTE = ['#2A63D6', '#E4693B', '#1F9254', '#D33B3B', '#14B8A6', '#D6A21A', '#5B5BD6', '#EC4899', '#0EA5E9', '#8B5CF6'];

export default function RiskCategoryDonut({ data = [], emptyHint = "Categorise risks by type and they'll appear here.", showBars = false, hideLegend = false }) {
    // Show every non-zero category (no top-N cap) so the ring + center total
    // account for all risks, not just the largest few.
    const slices = data.filter((s) => (s.value || 0) > 0);
    if (!slices.length) return <NoDataPage hint={emptyHint} />;

    const total = slices.reduce((a, s) => a + (s.value || 0), 0);
    const size = 150, r = 64, inner = 42, cx = size / 2, cy = size / 2;
    let acc = 0;
    const arcs = slices.map((s, i) => {
        const v = s.value || 0;
        const a0 = (acc / total) * 2 * Math.PI - Math.PI / 2;
        acc += v;
        const a1 = (acc / total) * 2 * Math.PI - Math.PI / 2;
        const large = (a1 - a0) > Math.PI ? 1 : 0;
        const x0 = cx + r * Math.cos(a0), y0 = cy + r * Math.sin(a0);
        const x1 = cx + r * Math.cos(a1), y1 = cy + r * Math.sin(a1);
        const xi0 = cx + inner * Math.cos(a0), yi0 = cy + inner * Math.sin(a0);
        const xi1 = cx + inner * Math.cos(a1), yi1 = cy + inner * Math.sin(a1);
        const d = `M ${x0} ${y0} A ${r} ${r} 0 ${large} 1 ${x1} ${y1} L ${xi1} ${yi1} A ${inner} ${inner} 0 ${large} 0 ${xi0} ${yi0} Z`;
        const t = (s.label || '').toLowerCase();
        const color = t === 'technical' ? '#4cba76' : t === 'financial' ? '#ffb147' : t === 'unknown' ? '#4e79a7' : (s.color || PALETTE[i % PALETTE.length]);
        return { d, color, label: s.label, value: v };
    });

    return (
        <div className="rp1_donut">
            <svg width={size} height={size} viewBox={`0 0 ${size} ${size}`} aria-label="Risks by category">
                {arcs.map((a, i) => (
                    <path key={i} d={a.d} fill={a.color} stroke="var(--color-surface)" strokeWidth="2" />
                ))}
                <text x={cx} y={cy - 2} textAnchor="middle" style={{ ...body({ fontWeight: fontWeight.bold }), fontSize: 22 }} fill="var(--color-text-primary)">{total}</text>
                <text x={cx} y={cy + 15} textAnchor="middle" style={caption()} letterSpacing="1" fill="var(--color-text-muted)">RISKS</text>
            </svg>
            {!hideLegend && (
            <div className={`rp1_legend${showBars ? ' rp1_legend--bars' : ''}`}>
                {arcs.map((a, i) => {
                    const pct = Math.round((a.value / total) * 100);
                    return (
                        <div className="rp1_legend_item" key={i}>
                            <div className="rp1_legend_row">
                                <Body as="span" className="rp1_legend_dot" style={{ background: a.color }} />
                                <Body as="span" className="rp1_legend_ellipsis" title={a.label} color="var(--color-text-secondary)" style={{ textTransform: 'capitalize' }}>{a.label}</Body>
                                <Body as="span" className="rp1_num" color="var(--color-text-primary)" weight="semibold">{a.value}</Body>
                                <Body as="span" className="rp1_num" color="var(--color-text-muted)">{pct}%</Body>
                            </div>
                            {showBars && (
                                <div className="rp1_legend_bar">
                                    <div className="rp1_legend_bar_fill" style={{ width: `${pct}%`, background: a.color }} />
                                </div>
                            )}
                        </div>
                    );
                })}
            </div>
            )}
        </div>
    );
}
